import axios, { AxiosRequestConfig } from 'axios'
import { hasValidRefreshToken, refreshAccessToken } from './oidc'
import { toastProblem } from './problemToast'

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || ''
const baseURL = API_BASE ? `${API_BASE.replace(/\/$/, '')}` : ''

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const readUserId = (): string | undefined => {
  const raw = safeGet('veridoc:user')
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw)
    return parsed?.id || parsed?.userId || undefined
  } catch {
    return undefined
  }
}

export const SUMMARY_STYLE_OPTIONS = [
  'LAW_FIRM',
  'BANKING',
  'HR',
  'MEDICAL',
  'PLAIN',
  'EXPLAIN_FIVE_YEAR_OLD',
  'EXPLAIN_NEW_EMPLOYEE',
  'EXPLAIN_CFO',
  'EXPLAIN_CTO',
  'EXPLAIN_FOUNDER',
  'EXPLAIN_LAWYER',
  'EXPLAIN_COMPLIANCE',
  'EXPLAIN_NEGOTIATOR',
  'EXPLAIN_NON_NATIVE'
] as const
export type SummaryStyle = typeof SUMMARY_STYLE_OPTIONS[number]
export const DEFAULT_SUMMARY_STYLE: SummaryStyle = 'LAW_FIRM'

export type PersonaPlaybook = {
  id: string
  name: string
  description?: string
  summaryStyle: SummaryStyle
  triggerConfig?: Record<string, any>
  actionConfig?: Record<string, any>
  active: boolean
  createdAt?: string
  updatedAt?: string
}

export type PersonaPlaybookPayload = {
  name: string
  description?: string
  summaryStyle: SummaryStyle
  triggerConfig?: Record<string, any>
  actionConfig?: Record<string, any>
  active?: boolean
}

export type SummaryStyleDetail = {
  label: string
  tagline: string
  helper: string
  icon: string
}

export const SUMMARY_STYLE_DETAILS: Record<SummaryStyle, SummaryStyleDetail> = {
  LAW_FIRM: {
    label: 'Law Firm',
    tagline: 'Clause-focused brief with legal tone.',
    helper: 'Emphasizes enforceability, clauses, and risk posture.',
    icon: '⚖️'
  },
  BANKING: {
    label: 'Banking',
    tagline: 'Risk/compliance oriented summary.',
    helper: 'Highlights exposure, covenants, and approval readiness.',
    icon: '🏦'
  },
  HR: {
    label: 'HR',
    tagline: 'Policy and people-first digest.',
    helper: 'Surfaces employee impact, policy gaps, and required actions.',
    icon: '🧑‍🤝‍🧑'
  },
  MEDICAL: {
    label: 'Medical',
    tagline: 'Clinical + compliance bedside summary.',
    helper: 'Extracts patient safety, regulatory items, and next steps.',
    icon: '🩺'
  },
  PLAIN: {
    label: 'Plain',
    tagline: 'Neutral, bullet-friendly recap.',
    helper: 'General audience prose without domain jargon.',
    icon: '📝'
  },
  EXPLAIN_FIVE_YEAR_OLD: {
    label: "Explain Like I'm 5",
    tagline: 'Storybook tone for maximum clarity.',
    helper: 'Turns clauses into simple analogies for non-experts.',
    icon: '🧸'
  },
  EXPLAIN_NEW_EMPLOYEE: {
    label: 'New Employee',
    tagline: 'Onboarding partner voice.',
    helper: 'Connects the doc to teams, policies, and first-week actions.',
    icon: '🆕'
  },
  EXPLAIN_CFO: {
    label: 'CFO',
    tagline: 'Finance-first scoreboard.',
    helper: 'Calls out cash impact, covenants, and audit readiness.',
    icon: '💰'
  },
  EXPLAIN_CTO: {
    label: 'CTO',
    tagline: 'Systems + reliability lens.',
    helper: 'Highlights SLAs, integrations, and security promises.',
    icon: '💻'
  },
  EXPLAIN_FOUNDER: {
    label: 'Founder',
    tagline: 'Vision and runway scan.',
    helper: 'Translates terms into growth, leverage, and brand impact.',
    icon: '🚀'
  },
  EXPLAIN_LAWYER: {
    label: 'Lawyer (Explain)',
    tagline: 'Concise counsel-to-counsel.',
    helper: 'Summarizes clause posture and escalation cues.',
    icon: '📚'
  },
  EXPLAIN_COMPLIANCE: {
    label: 'Compliance Officer',
    tagline: 'Audit-ready rundown.',
    helper: 'Maps obligations to controls, reporting, and evidence.',
    icon: '🛡️'
  },
  EXPLAIN_NEGOTIATOR: {
    label: 'Negotiator',
    tagline: 'Deal-table cheat sheet.',
    helper: 'Flags leverage, trade-offs, and fallback positions.',
    icon: '🤝'
  },
  EXPLAIN_NON_NATIVE: {
    label: 'Plain English',
    tagline: 'Friendly translator.',
    helper: 'Uses short sentences and definitions for non-native speakers.',
    icon: '🌍'
  },
}

export const SUMMARY_STYLE_STORAGE_KEY = 'veridoc:summary-style'
export const SUMMARY_PLAYBOOK_STORAGE_KEY = 'veridoc:summary-playbook'

export const isSummaryStyle = (value: unknown): value is SummaryStyle =>
  SUMMARY_STYLE_OPTIONS.includes(value as SummaryStyle)

export const coerceSummaryStyle = (value: string | null | undefined): SummaryStyle =>
  isSummaryStyle(value) ? (value as SummaryStyle) : DEFAULT_SUMMARY_STYLE

export const readStoredSummaryStyle = (): SummaryStyle => coerceSummaryStyle(safeGet(SUMMARY_STYLE_STORAGE_KEY))

export const persistSummaryStyle = (style: SummaryStyle): void => {
  try {
    localStorage.setItem(SUMMARY_STYLE_STORAGE_KEY, style)
  } catch (error) {
    console.warn('Failed to persist summary style', error)
  }
}

export const readStoredPlaybookId = (): string | null => safeGet(SUMMARY_PLAYBOOK_STORAGE_KEY)

export const persistPlaybookId = (playbookId: string | null): void => {
  try {
    if (!playbookId) {
      localStorage.removeItem(SUMMARY_PLAYBOOK_STORAGE_KEY)
    } else {
      localStorage.setItem(SUMMARY_PLAYBOOK_STORAGE_KEY, playbookId)
    }
  } catch (error) {
    console.warn('Failed to persist summary playbook', error)
  }
}

export type SummaryResponse = {
  summary?: string
  highlights?: string[]
  risks?: string[]
  clauses?: string[]
  meta?: Record<string, any>
}

export type UploadResult = {
  id: string
  name: string
  mimeType?: string
  sizeBytes?: number
  currentVersion?: number
}

export type BatchSummaryResult = {
  id: string
  response?: SummaryResponse
}

export type BatchSummaryResponse = {
  aggregateSummary?: string
  aggregate?: string
  results?: BatchSummaryResult[]
}

export type SummarizeTextPayload = {
  text: string
  useAi?: boolean
}

export type DocumentHealthBucket = {
  date: string
  channel: string
  statusCounts: Record<string, number>
  riskCounts: Record<string, number>
  averageRiskScore: number
}

export type DocumentMemorySummary = {
  insightsCaptured: number
  riskyChanges: number
  positiveShifts: number
  averageApprovalLikelihood: number
  lastCapturedAt?: string | null
}

export type DocumentMemoryInsight = {
  id: string
  newVersionId?: string | null
  newVersionNumber?: number | null
  previousVersionId?: string | null
  previousVersionNumber?: number | null
  capturedAt?: string | null
  capturedBy?: string | null
  riskScoreDelta?: number | null
  riskScoreNew?: number | null
  riskScorePrevious?: number | null
  approvalDelta?: number | null
  approvalLikelihood?: number | null
  mustFixDelta?: number | null
  negotiableDelta?: number | null
  changeMagnitude?: number | null
  flaggedAsRisk?: boolean
  changeSummary?: string | null
  diffHtml?: string | null
  diffSummary?: Record<string, any> | null
  metadata?: Record<string, any> | null
}

export type DocumentMemorySnapshot = {
  documentId: string
  totalVersions?: number | null
  workspaceId?: string | null
  orgId?: string | null
  summary: DocumentMemorySummary
  insights: DocumentMemoryInsight[]
}

export const POLICY_ASSET_TYPES = [
  'EMPLOYEE_SUMMARY',
  'EXECUTIVE_SUMMARY',
  'COMPLIANCE_SUMMARY',
  'AUDITOR_SUMMARY',
  'TRAINING_QUIZ',
  'COMPLIANCE_CHECKLIST',
  'CHANGE_LOG'
] as const
export type PolicyAssetType = typeof POLICY_ASSET_TYPES[number]

export const POLICY_ASSET_FORMATS = ['MARKDOWN', 'HTML', 'JSON'] as const
export type PolicyAssetFormat = typeof POLICY_ASSET_FORMATS[number]

export type PolicyAssetDto = {
  type: PolicyAssetType
  format: PolicyAssetFormat
  content: string
  metadata?: Record<string, any> | null
}

export type PolicyBundleDto = {
  id: string
  policyName: string
  sourceDocumentId?: string | null
  createdAt?: string | null
  assets: PolicyAssetDto[]
  metadata?: Record<string, any> | null
}

export type PolicyBundleResponseDto = {
  bundleId?: string | null
  bundle?: PolicyBundleDto | null
}

export type PolicyBuilderPayload = {
  text: string
  documentId?: string
  policyName?: string
  previousBundleId?: string
  includeQuiz?: boolean
  includeChecklist?: boolean
  includeChangeLog?: boolean
}

export type ClauseDriftAlert = {
  documentId: string
  documentName?: string
  versionNumber?: number
  observedAt?: string
  clauseType: string
  orgId?: string
  delta: number
  threshold: number
  severity: string
  templateId?: string
  templateName?: string
  baselinePreview?: string
  observedPreview?: string
  insights?: string[]
}

export type ClauseTemplatePayload = {
  clauseType: string
  orgId?: string | null
  name?: string | null
  description?: string | null
  templateText: string
  maxDelta?: number
  updatedBy?: string
}

export type ClauseTemplateResponse = ClauseTemplatePayload & {
  id: string
  templateText: string
  maxDelta: number
  createdAt?: string
  updatedAt?: string
}

export type ClauseLibraryTemplateSummary = {
  id?: string
  clauseType: string
  name?: string | null
  description?: string | null
  orgId?: string | null
  updatedBy?: string | null
  updatedAt?: string | null
  maxDelta?: number | null
  templateText?: string | null
}

export type ClauseLibraryReplacementStat = {
  clauseType: string
  count: number
}

export type ClauseLibraryCompatibilityStat = ClauseLibraryReplacementStat & {
  averageScore?: number | null
}

export type ClauseLibraryNegotiationSummary = {
  totalOccurrences: number
  acceptanceRate: number
  outcomes: Record<string, number>
  topReplacements: ClauseLibraryReplacementStat[]
  compatibleClauses: ClauseLibraryCompatibilityStat[]
}

export type ClauseLibraryFrequencyAnomaly = {
  date: string
  count: number
  zScore: number
}

export type ClauseLibraryFrequencySummary = {
  recentDaily: Record<string, number>
  anomalies: ClauseLibraryFrequencyAnomaly[]
}

export type ClauseLibraryEntry = {
  clauseType: string
  template?: ClauseLibraryTemplateSummary | null
  negotiation?: ClauseLibraryNegotiationSummary | null
  frequency?: ClauseLibraryFrequencySummary | null
  driftAlerts: ClauseDriftAlert[]
}

export type ClauseSample = {
  documentId: string
  versionId?: string | null
  clauseType: string
  severity?: string | null
  createdAt?: string | null
}

export type ClauseLibraryDetailResponse = {
  entry: ClauseLibraryEntry
  samples: ClauseSample[]
}

export type ClauseLibrarySnapshot = {
  orgId?: string | null
  generatedAt?: string | null
  clauses: ClauseLibraryEntry[]
}

export type ClauseLibraryDetailParams = {
  orgId?: string
  severity?: string
  limit?: number
  includeGlobal?: boolean
  driftLookbackDays?: number
}

export type LearningCenterHighlight = {
  title: string
  summary: string
  supportingDocuments: string[]
  observedAt?: string
}

export type LearningCenterTopicCluster = {
  topic: string
  description: string
  documentCount: number
  totalMentions: number
  momentum: number
  clauseTypes: string[]
  sampleDocuments: string[]
}

export type LearningCenterBestPractice = {
  topic: string
  title: string
  playbook: string
  steps: string[]
  evidenceDocuments: string[]
  confidenceScore: number
}

export type LearningCenterSignal = {
  label: string
  severity: string
  detail?: string
  observedAt?: string
  documentId?: string
  clauseType?: string
}

export type WorkspaceLearningCenterSnapshot = {
  orgId?: string | null
  workspaceId: string
  analyzedDocuments: number
  analyzedVersions: number
  highlights: LearningCenterHighlight[]
  topics: LearningCenterTopicCluster[]
  bestPractices: LearningCenterBestPractice[]
  recentSignals: LearningCenterSignal[]
  generatedAt?: string
}

export type CollectionIntelligenceFilters = {
  orgId?: string
  sources?: string[]
  tags?: string[]
  clauseTypes?: string[]
  ingestedAfter?: string
  ingestedBefore?: string
}

export type CollectionIntelligenceRequest = {
  documentIds?: string[]
  filters?: CollectionIntelligenceFilters
  insightLimit?: number
}

export type CollectionIntelligenceRiskInsight = {
  type: string
  severity: string
  count: number
  documentIds: string[]
}

export type CollectionIntelligenceClauseOutlier = {
  clauseType: string
  variant: string
  documentId?: string | null
  deltaPercent: number
}

export type CollectionIntelligenceConsistencyAlert = {
  topic: string
  inconsistentDocs: number
  recommendedAction: string
}

export type CollectionIntelligenceCoverageGap = {
  topic: string
  missingIn: number
  severity: string
}

export type CollectionIntelligenceTrendPoint = {
  date: string
  highRiskDocuments: number
  averageRiskScore: number
}

export type CollectionIntelligenceRecommendedAction = {
  label: string
  playbook: string[]
  evidenceDocuments: string[]
}

export type CollectionIntelligenceSupportingSample = {
  documentId?: string | null
  versionId?: string | null
  highlight: string
  severity: string
}

export type CollectionIntelligenceSummary = {
  dominantRisks: CollectionIntelligenceRiskInsight[]
  outlierClauses: CollectionIntelligenceClauseOutlier[]
  consistencyAlerts: CollectionIntelligenceConsistencyAlert[]
  coverageGaps: CollectionIntelligenceCoverageGap[]
}

export type CollectionIntelligenceResponse = {
  orgId?: string | null
  workspaceId: string
  analyzedDocuments: number
  analyzedVersions: number
  timeRange: { earliest?: string | null; latest?: string | null }
  summary: CollectionIntelligenceSummary
  trendline: CollectionIntelligenceTrendPoint[]
  recommendedActions: CollectionIntelligenceRecommendedAction[]
  supportingSamples: CollectionIntelligenceSupportingSample[]
  generatedAt?: string
}

export const NEGOTIATION_TONES = ['BUSINESS', 'LEGAL', 'CONCISE'] as const
export type NegotiationSuggestionTone = typeof NEGOTIATION_TONES[number]

export type NegotiationChecklistRequest = {
  versionId?: string | null
  focusAreas?: string[]
  includePlanB?: boolean
  suggestionTone?: NegotiationSuggestionTone
}

export type NegotiationChecklistEvidence = {
  clauseType?: string
  severity?: string
  snippet?: string
}

export type NegotiationChecklistMustFix = {
  title: string
  detail?: string
  impact?: string
  suggestedAction?: string | null
  planB?: string | null
  evidence?: NegotiationChecklistEvidence | null
}

export type NegotiationChecklistNegotiableRisk = {
  title: string
  detail?: string
  recommendedPositions: string[]
  impact?: string
  planB?: string | null
  evidence?: NegotiationChecklistEvidence | null
}

export type NegotiationChecklistAlternative = {
  topic: string
  currentText?: string | null
  suggestedText?: string | null
  rationale?: string | null
}

export type NegotiationChecklistMissingItem = {
  topic: string
  impact?: string
  suggestedText?: string
}

export type NegotiationChecklistBestPractice = {
  topic: string
  steps: string[]
  confidence: number
}

export type NegotiationChecklistMetadata = {
  riskScore: number
  highRiskClauses: number
  missingRequiredSections: number
}

export type NegotiationChecklistResponse = {
  documentId: string
  versionId?: string | null
  generatedAt?: string
  mustFix: NegotiationChecklistMustFix[]
  negotiableRisks: NegotiationChecklistNegotiableRisk[]
  alternativeWordings: NegotiationChecklistAlternative[]
  missingItems: NegotiationChecklistMissingItem[]
  bestPractices: NegotiationChecklistBestPractice[]
  metadata: NegotiationChecklistMetadata
}

export type DocumentTimelineRequest = {
  versionId?: string | null
  focusOwners?: string[]
  focusTopics?: string[]
  maxEvents?: number
  lookaheadDays?: number
  includeRecurring?: boolean
}

export type DocumentTimelineDueWindow = {
  absoluteDate?: string | null
  relativeWindow?: string | null
  interval?: string | null
  daysFromEffectiveDate?: number | null
  timezone?: string | null
}

export type DocumentTimelineEvent = {
  id: string
  label: string
  owner?: string
  department?: string | null
  category?: string
  riskLevel?: string
  recurring: boolean
  due: DocumentTimelineDueWindow
  obligations: string[]
  reminders: string[]
  tags: string[]
  source?: string
  confidenceScore: number
}

export type DocumentTimelineOwnerCoverage = {
  owner: string
  events: number
}

export type DocumentTimelineCategoryCoverage = {
  category: string
  events: number
}

export type DocumentTimelineSummary = {
  totalEvents: number
  highRiskEvents: number
  nextDeadline?: string | null
  ownerCoverage: DocumentTimelineOwnerCoverage[]
  categoryCoverage: DocumentTimelineCategoryCoverage[]
}

export type DocumentTimelineResponse = {
  documentId: string
  versionId?: string | null
  generatedAt?: string
  events: DocumentTimelineEvent[]
  summary: DocumentTimelineSummary
}

export type ContractSimulationRequest = {
  versionId?: string | null
  focusClauses?: string[]
  scenarioAdjustments?: string[]
  includeComparables?: boolean
  lookbackMonths?: number
  simulationHorizonDays?: number
}

export type ContractSimulationClauseProjection = {
  clauseType: string
  issueCategory?: string | null
  disputeProbability: number
  renewalImpact: number
  riskLevel: string
  drivers: string[]
  recommendations: string[]
}

export type ContractSimulationInsight = {
  title: string
  severity?: string | null
  description: string
  recommendedActions: string[]
}

export type ContractSimulationComparable = {
  referenceId: string
  label: string
  similarityScore: number
  resolvedOutcome?: string | null
  riskScore?: number | null
  renewalResult?: number | null
}

export type ContractSimulationConfidence = {
  total: number
  evidence: string[]
  dataAnchors: string[]
}

export type ContractSimulationResponse = {
  documentId: string
  versionId?: string | null
  generatedAt?: string
  disputeProbability: number
  estimatedDisputeCost: number
  renewalProbability: number
  nonComplianceRisk: number
  negotiationPushbackProbability: number
  currency: string
  clauseHotspots: ContractSimulationClauseProjection[]
  insights: ContractSimulationInsight[]
  comparableContracts: ContractSimulationComparable[]
  confidence: ContractSimulationConfidence
}

export type InstantComparisonObligationPanel = {
  totalEvents: number
  highRiskEvents: number
  dueSoonEvents: number
  nextDeadline?: string | null
  dominantOwners: string[]
  urgentActions: string[]
}

export type InstantComparisonRiskPanel = {
  riskScore?: number | null
  riskLevel?: string | null
  criticalRisks: string[]
  watchItems: string[]
}

export type InstantComparisonFinancialPanel = {
  contractValue?: number | null
  annualValue?: number | null
  terminationFees?: number | null
  currency?: string | null
  callouts: string[]
}

export type InstantComparisonPartySnapshot = {
  label: string
  documentId: string
  documentName: string
  versionId: string
  versionNumber?: number | null
  favorabilityScore?: number | null
  obligations: InstantComparisonObligationPanel
  risk: InstantComparisonRiskPanel
  financial: InstantComparisonFinancialPanel
  highlights: string[]
}

export type InstantComparisonCallout = {
  dimension: string
  title: string
  detail: string
  favoredParty: string
}

export type InstantComparisonSummary = {
  favoredParty: string
  favorabilityDelta: number
  callouts: InstantComparisonCallout[]
}

export type InstantComparisonResponse = {
  generatedAt: string
  left: InstantComparisonPartySnapshot
  right: InstantComparisonPartySnapshot
  summary: InstantComparisonSummary
}

export type InstantComparisonRequest = {
  leftDocumentId: string
  rightDocumentId: string
  leftLabel?: string
  rightLabel?: string
  leftVersionId?: string
  rightVersionId?: string
  focusOwners?: string[]
  focusTopics?: string[]
  maxEvents?: number
  lookaheadDays?: number
  includeRecurring?: boolean
}

export type DecisionBriefVerdict = {
  approve: boolean
  confidence: number
  rationale: string
}

export type DecisionBriefImpact = {
  level: string
  narrative: string
}

export type DecisionBriefSummaryDigest = {
  headline: string
  overview: string
  highlights: string[]
}

export type DecisionBriefNegotiationDigest = {
  riskScore: number
  mustFixCount: number
  negotiableCount: number
}

export type DecisionBriefTimelineDigest = {
  totalEvents: number
  highRiskEvents: number
  nextDeadline?: string | null
}

export type DecisionBriefRawArtifacts = {
  summary?: SummaryResponse | null
  checklist?: NegotiationChecklistResponse | null
  timeline?: DocumentTimelineResponse | null
}

export type DecisionBriefHistoryEntry = {
  id: string
  generatedAt: string
  approve: boolean
  confidence: number
  rationale: string
  focusAreas: string[]
  personaBrief?: string | null
}

export type DecisionBriefEvidenceCitation = {
  type: string
  label: string
  snippet?: string | null
  referenceId?: string | null
  severity?: string | null
}

export type DecisionBriefDecisionItem = {
  id: string
  title: string
  category: string
  status: string
  score: number
  summary: string
  owner: string
  evidence: DecisionBriefEvidenceCitation[]
  recommendedActions: string[]
}

export type DecisionBriefActionItem = {
  id: string
  title: string
  owner: string
  severity: string
  dueDate?: string | null
  rationale: string
  references: string[]
}

export type DecisionBriefScorecard = {
  score: number
  band: string
  clauseCoverage: number
  missingCriticalClauses: number
  riskDensity: number
  negotiationPressure: number
  timelinePressure: number
  clauseCoverageDiagnostics: Record<string, number>
  riskHistogram: Record<string, number>
  conflictingClauses: number
  conflictsByClause: Record<string, number>
  unknownClauseTypes: string[]
}

export type DecisionBriefResponse = {
  documentId: string
  workspaceId: string
  versionId?: string | null
  generatedAt?: string
  decision: DecisionBriefVerdict
  benefits: string[]
  risks: string[]
  dealBreakers: string[]
  criticalQuestions: string[]
  financialImpact: DecisionBriefImpact
  complianceImpact: DecisionBriefImpact
  liabilityImpact: DecisionBriefImpact
  recommendedNegotiationPoints: string[]
  rejectionAlternatives: string[]
  summary: DecisionBriefSummaryDigest
  negotiation: DecisionBriefNegotiationDigest
  timeline: DecisionBriefTimelineDigest
  topDecisions: DecisionBriefDecisionItem[]
  businessSummary: string[]
  immediateActions: DecisionBriefActionItem[]
  scorecard: DecisionBriefScorecard
  decisionMatrix: DecisionBriefDecisionItem[]
  actionItems: DecisionBriefActionItem[]
  meta?: Record<string, any>
  rawArtifacts?: DecisionBriefRawArtifacts | null
  history: DecisionBriefHistoryEntry[]
}

export type DecisionBriefRequestPayload = {
  versionId?: string | null
  focusAreas?: string[]
  personaBrief?: string | null
  includeRawArtifacts?: boolean
  documentTypeOverride?: string | null
  templateId?: string | null
  includeDecisionMatrix?: boolean
}

export type WhatChangedIfRequest = {
  versionId?: string | null
  clauseId?: string | null
  clauseType?: string | null
  changeSummary?: string | null
  currentText?: string | null
  proposedText?: string | null
  focusDimensions?: string[]
  stakeholderRoles?: string[]
  includeCounterpartySignals?: boolean
  includeLinkedObligations?: boolean
}

export type WhatChangedIfClauseInsight = {
  clauseId?: string | null
  clauseType?: string | null
  title: string
  currentTerm?: string | null
  proposedTerm?: string | null
  severity: string
  confidence: number
  keywords: string[]
  jurisdictions: string[]
}

export type WhatChangedIfImpactAssessment = {
  dimension: string
  severity: string
  narrative: string
  drivers: string[]
  confidence: number
  recommendedActions: string[]
}

export type WhatChangedIfCounterpartySignal = {
  party?: string | null
  sentiment?: string | null
  rationale?: string | null
  lastSeenVersion?: string | null
  observedAt?: string | null
}

export type WhatChangedIfLinkedObligation = {
  label: string
  dueDate?: string | null
  owner?: string | null
  riskLevel?: string | null
  dependency?: string | null
}

export type WhatChangedIfPlaybookGuidance = {
  guardrails: string[]
  fallbackPositions: string[]
  monitoringActions: string[]
}

export type WhatChangedIfImpactSummary = {
  highRiskCount: number
  mediumRiskCount: number
  flaggedDomains: string[]
  volatilityScore: number
  overallRecommendation: string
}

export type WhatChangedIfResponse = {
  documentId: string
  versionId?: string | null
  generatedAt?: string
  clause: WhatChangedIfClauseInsight
  impacts: WhatChangedIfImpactAssessment[]
  counterpartySignals: WhatChangedIfCounterpartySignal[]
  linkedObligations: WhatChangedIfLinkedObligation[]
  guidance: WhatChangedIfPlaybookGuidance
  summary: WhatChangedIfImpactSummary
}

export type AutomationTask = {
  id: string
  tenantId?: string
  workspaceId: string
  documentId: string
  versionId?: string | null
  eventId?: string | null
  title: string
  assignee?: string | null
  assigneeEmail?: string | null
  department?: string | null
  category?: string | null
  priority?: string | null
  recurring: boolean
  recurrenceRule?: string | null
  reminderChannel?: string | null
  confidence: number
  instructions?: string | null
  dueAt?: string | null
  nextTriggerAt?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  completedAt?: string | null
  status: string
  source?: string | null
  metadata?: Record<string, any> | null
}

export type CopilotCitation = {
  sourcePath?: string
  jsonPointer?: string
  startOffset?: number
  endOffset?: number
  score?: number
}

export type CopilotChatPayload = {
  message: string
  userId?: string
  sessionId?: string
  context?: string
  topK?: number
}

export type CopilotChatResponse = {
  answer: string
  citations?: CopilotCitation[]
  confidence?: number
  interactionId?: string
  patternKey?: string
  tookMs?: number
  usedLlm?: boolean
  context?: string
}

export const listPersonaPlaybooks = (activeOnly = true) =>
  api.get<PersonaPlaybook[]>('/playbooks', { params: { activeOnly } })

export const createPersonaPlaybook = (payload: PersonaPlaybookPayload) =>
  api.post<PersonaPlaybook>('/playbooks', payload)

export const updatePersonaPlaybook = (id: string, payload: PersonaPlaybookPayload) =>
  api.put<PersonaPlaybook>(`/playbooks/${id}`, payload)

export const archivePersonaPlaybook = (id: string) =>
  api.delete<void>(`/playbooks/${id}`)

const withPlaybook = <T extends Record<string, any>>(payload: T, playbookId?: string | null): T & { playbookId?: string } => {
  if (!playbookId) return payload
  return { ...payload, playbookId }
}

export const api = axios.create({
  baseURL: baseURL || '/api',
  withCredentials: false,
})

// Attach Authorization header from SPA token storage (if present)
api.interceptors.request.use((cfg) => {
  try {
    const token = localStorage.getItem('veridoc:access_token')
    if (token) {
      cfg.headers = cfg.headers || {}
      ;(cfg.headers as any)['Authorization'] = `Bearer ${token}`
    }
    const tenantId = safeGet('veridoc:org') || safeGet('veridoc:tenant')
    const workspaceId = safeGet('veridoc:workspace')
    const userId = readUserId()
    if (tenantId) {
      cfg.headers = cfg.headers || {}
      ;(cfg.headers as any)['X-Tenant-Id'] = tenantId
    }
    if (workspaceId) {
      cfg.headers = cfg.headers || {}
      ;(cfg.headers as any)['X-Workspace-Id'] = workspaceId
    }
    if (userId) {
      cfg.headers = cfg.headers || {}
      ;(cfg.headers as any)['X-User-Id'] = userId
    }
    // Add API key for production authentication
    cfg.headers = cfg.headers || {}
    ;(cfg.headers as any)['X-API-Key'] = 'prod.prefix.secret1'
  } catch (error) {
    console.warn('Failed to attach authentication headers', error)
  }
  return cfg
})

// Single-flight refresh handling
let refreshPromise: Promise<void> | null = null
const REFRESH_URL = '/ui/refresh'

async function performRefresh() {
  // Prefer OIDC refresh if a refresh token is present, else fallback to backend session refresh
  try {
    if (hasValidRefreshToken()) {
      const ok = await refreshAccessToken()
      if (!ok) throw new Error('refresh_token missing or invalid')
      return
    }
  } catch (error) {
    console.warn('OIDC access token refresh failed', error)
    throw error
  }
  // Fallback
  await api.post(REFRESH_URL)
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Report to Sentry with structured context (if available)
    try {
      const sentryDsn = (import.meta as any).env?.VITE_SENTRY_DSN
      if (sentryDsn) {
        const sentry = await import('@sentry/react')
        const cfg: AxiosRequestConfig & { _retry?: boolean } = err?.config || {}
        const reqUrl: string = (cfg?.url as string) || ''
        const method = (cfg?.method || 'get').toString().toUpperCase()
        const status = err?.response?.status
        const data = err?.response?.data
        const headers = err?.response?.headers
        sentry.captureException(err, {
          tags: { area: 'api', method, status: String(status || 'n/a') },
          contexts: {
            request: { url: reqUrl, method, baseURL: (cfg as any)?.baseURL },
            response: { status, headers, sample: typeof data === 'object' ? JSON.stringify(data).slice(0, 2000) : String(data).slice(0, 2000) }
          }
        })
      }
    } catch (error) {
      console.warn('Failed to report API error to Sentry', error)
    }
    // Offline fast-fail hint
    if (!err?.response) {
      if (typeof navigator !== 'undefined' && navigator && (navigator as any).onLine === false) {
        console.warn('Network offline; request failed')
      }
    }
    // RFC7807 problem+json surface (if present)
    const ct = err?.response?.headers?.['content-type'] || ''
    if (ct.includes('application/problem+json')) {
      try {
        toastProblem(err.response.data)
      } catch (error) {
        console.warn('Failed to display problem response', error)
      }
    }
    // Authz handling with refresh single-flight
    const status = err?.response?.status
    const cfg: AxiosRequestConfig & { _retry?: boolean } = err?.config || {}
    const reqUrl: string = (cfg?.url as string) || ''

    if (status === 401 && !cfg._retry && !reqUrl.endsWith(REFRESH_URL)) {
      cfg._retry = true
      if (!refreshPromise) {
        refreshPromise = performRefresh()
          .catch((e) => { throw e })
          .finally(() => { refreshPromise = null })
      }
      try {
        await refreshPromise
        // retry original request
        return api.request(cfg)
      } catch (error) {
        console.warn('Refresh token flow failed; redirecting to login', error)
        // Refresh failed → redirect to login
        const next = `${location.pathname}${location.search}`
        const url = `/login?next=${encodeURIComponent(next)}`
        if (location.pathname !== '/login') location.assign(url)
      }
    } else if (status === 403) {
      if (location.pathname !== '/forbidden') location.assign('/forbidden')
    }
    return Promise.reject(err)
  }
)

export const requestDocumentSummary = (docId: string, style: SummaryStyle, playbookId?: string | null) =>
  api.post<SummaryResponse>(`/veridoc/documents/${docId}/summarize`, withPlaybook({ style }, playbookId))

export const requestBatchSummary = (ids: string[], style: SummaryStyle, playbookId?: string | null) =>
  api.post<BatchSummaryResponse>('/veridoc/documents/batch-summarize', withPlaybook({ ids, style }, playbookId))

export const requestBatchSummaryAsync = (ids: string[], style: SummaryStyle, playbookId?: string | null) =>
  api.post<{ jobId: string }>('/veridoc/documents/batch-summarize-async', withPlaybook({ ids, style }, playbookId))

export const requestBatchSummaryStatus = (jobId: string) =>
  api.get<{ status: string; result?: BatchSummaryResponse; error?: string }>(`/veridoc/documents/batch-summarize/${jobId}`)

export const requestTextSummary = (payload: SummarizeTextPayload, style: SummaryStyle, playbookId?: string | null) =>
  api.post<SummaryResponse>('/ui/summarize', withPlaybook({ ...payload, style }, playbookId))

export const createPolicyBundle = (payload: PolicyBuilderPayload) => {
  const body: PolicyBuilderPayload = {
    ...payload,
    text: payload?.text?.trim() || '',
    documentId: payload?.documentId?.trim() || undefined,
    policyName: payload?.policyName?.trim() || undefined,
    previousBundleId: payload?.previousBundleId?.trim() || undefined,
  }
  if (!body.text) {
    return Promise.reject(new Error('text is required to build a policy bundle'))
  }
  return api.post<PolicyBundleResponseDto>('/policies/bundles', body)
}

export const fetchPolicyBundle = (bundleId: string) => {
  const trimmed = bundleId?.trim()
  if (!trimmed) {
    return Promise.reject(new Error('bundleId is required'))
  }
  return api.get<PolicyBundleResponseDto>(`/policies/bundles/${encodeURIComponent(trimmed)}`)
}

export const listPolicyBundles = (documentId: string) => {
  const trimmed = documentId?.trim()
  if (!trimmed) {
    return Promise.reject(new Error('documentId is required'))
  }
  return api.get<PolicyBundleResponseDto[]>('/policies/bundles', { params: { documentId: trimmed } })
}

export const fetchDocumentMemorySnapshot = (documentId: string, limit = 6) => {
  const trimmed = documentId?.trim()
  if (!trimmed) {
    return Promise.reject(new Error('documentId is required'))
  }
  const params: Record<string, number> = {}
  if (typeof limit === 'number' && limit > 0) params.limit = limit
  return api.get<DocumentMemorySnapshot>(`/veridoc/documents/${encodeURIComponent(trimmed)}/memory`, {
    params: Object.keys(params).length ? params : undefined,
  })
}

export const getDocumentHealthHeatmap = (orgId?: string, lookbackDays?: number) => {
  const params: Record<string, any> = {}
  if (orgId && orgId.trim().length > 0) params.orgId = orgId.trim()
  if (typeof lookbackDays === 'number' && lookbackDays > 0) params.lookbackDays = lookbackDays
  return api.get<DocumentHealthBucket[]>('/analytics/document-health', { params })
}

export const getClauseDriftAlerts = (orgId?: string, lookbackDays?: number, minDelta?: number) => {
  const params: Record<string, any> = {}
  if (orgId && orgId.trim().length > 0) params.orgId = orgId.trim()
  if (typeof lookbackDays === 'number' && lookbackDays > 0) params.lookbackDays = lookbackDays
  if (typeof minDelta === 'number') params.minDelta = minDelta
  return api.get<ClauseDriftAlert[]>('/analytics/clause-drift', { params })
}

export const listClauseTemplates = (orgId?: string, includeGlobal = true) => {
  const params: Record<string, any> = {}
  if (orgId && orgId.trim().length > 0) params.orgId = orgId.trim()
  params.includeGlobal = includeGlobal
  return api.get<ClauseTemplateResponse[]>('/analytics/clause-templates', { params })
}

export const createClauseTemplate = (payload: ClauseTemplatePayload) =>
  api.post<ClauseTemplateResponse>('/analytics/clause-templates', payload)

export const updateClauseTemplate = (id: string, payload: ClauseTemplatePayload) =>
  api.put<ClauseTemplateResponse>(`/analytics/clause-templates/${id}`, payload)

export const deleteClauseTemplate = (id: string) =>
  api.delete<void>(`/analytics/clause-templates/${id}`)

export const getClauseLibrarySnapshot = (orgId?: string, includeGlobal = true, driftLookbackDays = 45) => {
  const params: Record<string, string | number | boolean> = {
    includeGlobal,
    driftLookbackDays,
  }
  if (orgId && orgId.trim().length > 0) {
    params.orgId = orgId.trim()
  }
  return api.get<ClauseLibrarySnapshot>('/clauses/library', { params })
}

export const getClauseLibraryDetail = (
  clauseType: string,
  options: ClauseLibraryDetailParams = {}
) => {
  const trimmedType = clauseType?.trim()
  if (!trimmedType) {
    return Promise.reject(new Error('clauseType is required'))
  }
  const params: Record<string, string | number | boolean> = {}
  if (options.orgId && options.orgId.trim().length > 0) {
    params.orgId = options.orgId.trim()
  }
  if (options.severity && options.severity.trim().length > 0) {
    params.severity = options.severity.trim()
  }
  if (typeof options.limit === 'number' && options.limit > 0) {
    params.limit = Math.min(100, Math.max(1, Math.floor(options.limit)))
  }
  if (typeof options.includeGlobal === 'boolean') {
    params.includeGlobal = options.includeGlobal
  }
  if (typeof options.driftLookbackDays === 'number' && options.driftLookbackDays > 0) {
    params.driftLookbackDays = options.driftLookbackDays
  }
  return api.get<ClauseLibraryDetailResponse>(`/clauses/library/${encodeURIComponent(trimmedType)}`, {
    params: Object.keys(params).length ? params : undefined,
  })
}

export const getWorkspaceLearningCenter = (workspaceId: string, lookbackDays?: number) => {
  const trimmed = workspaceId?.trim()
  if (!trimmed) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  const params: Record<string, number> = {}
  if (typeof lookbackDays === 'number' && lookbackDays > 0) params.lookbackDays = lookbackDays
  return api.get<WorkspaceLearningCenterSnapshot>(`/workspaces/${encodeURIComponent(trimmed)}/learning-center`, {
    params: Object.keys(params).length ? params : undefined,
  })
}

export const runCollectionIntelligence = (workspaceId: string, payload: CollectionIntelligenceRequest) => {
  const trimmed = workspaceId?.trim()
  if (!trimmed) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  const requestPayload: CollectionIntelligenceRequest = {
    documentIds: payload?.documentIds?.length ? payload.documentIds : undefined,
    insightLimit: payload?.insightLimit,
    filters: payload?.filters,
  }
  return api.post<CollectionIntelligenceResponse>(
    `/workspaces/${encodeURIComponent(trimmed)}/collections/intelligence`,
    requestPayload
  )
}

const INGESTION_SOURCES = ['s3', 'email', 'sftp'] as const
export type IngestionScanSource = typeof INGESTION_SOURCES[number]

const isValidIngestionSource = (value: string): value is IngestionScanSource =>
  INGESTION_SOURCES.some((entry) => entry === value)

export const triggerIngestionScan = (source: IngestionScanSource = 's3') => {
  const normalized = (source || 's3').toLowerCase()
  if (!isValidIngestionSource(normalized)) {
    return Promise.reject(new Error('Unsupported ingestion source'))
  }
  return api.post<string>(`/veridoc/ingest/${normalized}/scan`).then((res) => res.data)
}

export const runNegotiationChecklist = (workspaceId: string, documentId: string, payload: NegotiationChecklistRequest) => {
  const ws = workspaceId?.trim()
  const doc = documentId?.trim()
  if (!ws) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  if (!doc) {
    return Promise.reject(new Error('documentId is required'))
  }
  const sanitizedFocusAreas = payload?.focusAreas
    ?.map((entry) => entry?.trim())
    .filter((entry): entry is string => Boolean(entry && entry.length > 0))
  const cleaned: NegotiationChecklistRequest = {
    versionId: payload?.versionId?.trim() || undefined,
    focusAreas: sanitizedFocusAreas,
    includePlanB: payload?.includePlanB,
    suggestionTone: payload?.suggestionTone,
  }
  if (!cleaned.focusAreas?.length) {
    delete cleaned.focusAreas
  }
  return api.post<NegotiationChecklistResponse>(
    `/workspaces/${encodeURIComponent(ws)}/documents/${encodeURIComponent(doc)}/negotiation/checklist`,
    cleaned
  )
}

const sanitizeList = (values?: string[]) =>
  values
    ?.map((entry) => entry?.trim().toLowerCase())
    .filter((entry): entry is string => Boolean(entry && entry.length > 0))

const clampNumber = (value: number | undefined, min: number, max: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined
  return Math.min(max, Math.max(min, value))
}

export const generateDocumentTimeline = (workspaceId: string, documentId: string, payload: DocumentTimelineRequest) => {
  const ws = workspaceId?.trim()
  const doc = documentId?.trim()
  if (!ws) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  if (!doc) {
    return Promise.reject(new Error('documentId is required'))
  }
  const owners = sanitizeList(payload?.focusOwners)
  const topics = sanitizeList(payload?.focusTopics)
  const body: DocumentTimelineRequest = {
    versionId: payload?.versionId?.trim() || undefined,
    focusOwners: owners && owners.length ? owners : undefined,
    focusTopics: topics && topics.length ? topics : undefined,
    includeRecurring: payload?.includeRecurring,
    maxEvents: clampNumber(payload?.maxEvents, 10, 200),
    lookaheadDays: clampNumber(payload?.lookaheadDays, 30, 3650),
  }
  if (!body.maxEvents) {
    delete body.maxEvents
  }
  if (!body.lookaheadDays) {
    delete body.lookaheadDays
  }
  return api.post<DocumentTimelineResponse>(
    `/workspaces/${encodeURIComponent(ws)}/documents/${encodeURIComponent(doc)}/timeline`,
    body
  )
}

export const simulateContractOutcomes = (workspaceId: string, documentId: string, payload: ContractSimulationRequest) => {
  const ws = workspaceId?.trim()
  const doc = documentId?.trim()
  if (!ws) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  if (!doc) {
    return Promise.reject(new Error('documentId is required'))
  }
  const clauses = sanitizeList(payload?.focusClauses)
  const adjustments = sanitizeList(payload?.scenarioAdjustments)
  const lookback = clampNumber(payload?.lookbackMonths, 1, 60)
  const horizon = clampNumber(payload?.simulationHorizonDays, 30, 1095)
  const body: ContractSimulationRequest = {
    versionId: payload?.versionId?.trim() || undefined,
    focusClauses: clauses && clauses.length ? clauses : undefined,
    scenarioAdjustments: adjustments && adjustments.length ? adjustments : undefined,
    includeComparables: payload?.includeComparables,
    lookbackMonths: typeof lookback === 'number' ? lookback : undefined,
    simulationHorizonDays: typeof horizon === 'number' ? horizon : undefined,
  }
  return api.post<ContractSimulationResponse>(
    `/workspaces/${encodeURIComponent(ws)}/documents/${encodeURIComponent(doc)}/simulator/outcomes`,
    body
  )
}

export const runInstantComparison = (workspaceId: string, payload: InstantComparisonRequest) => {
  const ws = workspaceId?.trim()
  const leftDoc = payload?.leftDocumentId?.trim()
  const rightDoc = payload?.rightDocumentId?.trim()
  if (!ws) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  if (!leftDoc || !rightDoc) {
    return Promise.reject(new Error('Two documentIds are required'))
  }
  const owners = sanitizeList(payload?.focusOwners)
  const topics = sanitizeList(payload?.focusTopics)
  const maxEvents = clampNumber(payload?.maxEvents, 20, 200)
  const lookaheadDays = clampNumber(payload?.lookaheadDays, 30, 3650)
  const body: InstantComparisonRequest = {
    leftDocumentId: leftDoc,
    rightDocumentId: rightDoc,
    leftLabel: payload?.leftLabel?.trim() || undefined,
    rightLabel: payload?.rightLabel?.trim() || undefined,
    leftVersionId: payload?.leftVersionId?.trim() || undefined,
    rightVersionId: payload?.rightVersionId?.trim() || undefined,
    includeRecurring: payload?.includeRecurring,
    maxEvents: maxEvents || undefined,
    lookaheadDays: lookaheadDays || undefined,
  }
  if (owners && owners.length) {
    body.focusOwners = owners
  }
  if (topics && topics.length) {
    body.focusTopics = topics
  }
  return api.post<InstantComparisonResponse>(
    `/workspaces/${encodeURIComponent(ws)}/compare/instant`,
    body
  )
}

export const listAutomationTasks = (workspaceId: string, documentId: string) => {
  const ws = workspaceId?.trim()
  const doc = documentId?.trim()
  if (!ws) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  if (!doc) {
    return Promise.reject(new Error('documentId is required'))
  }
  return api.get<AutomationTask[]>(
    `/workspaces/${encodeURIComponent(ws)}/documents/${encodeURIComponent(doc)}/tasks/automation`
  )
}

export const generateDecisionBrief = (workspaceId: string, documentId: string, payload?: DecisionBriefRequestPayload) => {
  const ws = workspaceId?.trim()
  const doc = documentId?.trim()
  if (!ws) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  if (!doc) {
    return Promise.reject(new Error('documentId is required'))
  }
  const focusAreas = payload?.focusAreas
    ?.map((entry) => entry?.trim())
    .filter((entry): entry is string => Boolean(entry && entry.length > 0))
  const body: DecisionBriefRequestPayload = {}
  const versionId = payload?.versionId?.trim()
  if (versionId) {
    body.versionId = versionId
  }
  if (focusAreas && focusAreas.length) {
    body.focusAreas = focusAreas
  }
  const personaBrief = payload?.personaBrief?.trim()
  if (personaBrief) {
    body.personaBrief = personaBrief
  }
  if (payload?.includeRawArtifacts) {
    body.includeRawArtifacts = true
  }
  const docTypeOverride = payload?.documentTypeOverride?.trim()
  if (docTypeOverride) {
    body.documentTypeOverride = docTypeOverride.toUpperCase()
  }
  const templateId = payload?.templateId?.trim()
  if (templateId) {
    body.templateId = templateId
  }
  if (typeof payload?.includeDecisionMatrix === 'boolean') {
    body.includeDecisionMatrix = payload.includeDecisionMatrix
  }
  return api.post<DecisionBriefResponse>(
    `/workspaces/${encodeURIComponent(ws)}/documents/${encodeURIComponent(doc)}/decision-brief`,
    body
  )
}

export const generateWhatChangedIf = (workspaceId: string, documentId: string, payload: WhatChangedIfRequest) => {
  const ws = workspaceId?.trim()
  const doc = documentId?.trim()
  if (!ws) {
    return Promise.reject(new Error('workspaceId is required'))
  }
  if (!doc) {
    return Promise.reject(new Error('documentId is required'))
  }
  const dimensions = sanitizeList(payload?.focusDimensions)
  const stakeholders = sanitizeList(payload?.stakeholderRoles)
  const clean = (value?: string | null) => value?.trim() || undefined
  const body: WhatChangedIfRequest = {
    versionId: clean(payload?.versionId),
    clauseId: clean(payload?.clauseId),
    clauseType: clean(payload?.clauseType),
    changeSummary: clean(payload?.changeSummary),
    currentText: clean(payload?.currentText),
    proposedText: clean(payload?.proposedText),
    focusDimensions: dimensions && dimensions.length ? dimensions : undefined,
    stakeholderRoles: stakeholders && stakeholders.length ? stakeholders : undefined,
    includeCounterpartySignals: payload?.includeCounterpartySignals,
    includeLinkedObligations: payload?.includeLinkedObligations,
  }
  return api.post<WhatChangedIfResponse>(
    `/workspaces/${encodeURIComponent(ws)}/documents/${encodeURIComponent(doc)}/what-if/analyzer`,
    body
  )
}

export const postCopilotChat = (payload: CopilotChatPayload) => {
  const chatUrl = '/copilot-chat'
  const overrideBase = API_BASE ? API_BASE.replace(/\/$/, '') : ''
  return api.post<CopilotChatResponse>(chatUrl, payload, { baseURL: overrideBase })
}

export type ComplianceEventPayload = {
  subject: string
  consentType: string
  granted?: boolean
  metadata?: Record<string, any> | string | null
}

export const recordComplianceEvent = (payload: ComplianceEventPayload) => {
  const subject = payload?.subject?.trim()
  const consentType = payload?.consentType?.trim()
  if (!subject) {
    return Promise.reject(new Error('subject is required'))
  }
  if (!consentType) {
    return Promise.reject(new Error('consentType is required'))
  }
  const body: Record<string, any> = {
    subject,
    consentType,
    granted: payload?.granted ?? true,
  }
  if (payload?.metadata) {
    body.metadata = typeof payload.metadata === 'string' ? payload.metadata : JSON.stringify(payload.metadata)
  }
  return api.post<void>('/compliance/consents', body)
}
